import { CalendarOutlined, CarOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { Alert, Empty, Form, Select, Spin } from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { BsShieldFillCheck, BsShieldFillExclamation } from "react-icons/bs";
import CustomDateTimePicker from "../../../../../components/Shared/CustomDateTimePicker";
import {
    getLocationLabel,
    getLocationMongoId,
    PROVIDER_LABELS,
} from "../../../lib/insuranceSaleApi";
import SellableCarCard from "../shared/SellableCarCard";
import cn from "../../../../../utilities/tailwind-merge";

/** A titled card for one part of the step; `muted` dims it until its prerequisite is chosen. */
const StepSection = ({ icon, title, hint, muted, children }) => (
    <section className={cn("rounded-xl border border-gray-200 bg-white p-4 sm:p-5", muted && "bg-gray-50/60")}>
        <div className="mb-3 flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#F0F0FE] text-base text-[#5D5DF6]">
                {icon}
            </span>
            <div className="min-w-0">
                <h4 className="m-0 text-base font-semibold text-gray-900">{title}</h4>
                {hint ? <p className="m-0 mt-0.5 text-sm text-gray-500">{hint}</p> : null}
            </div>
        </div>
        {children}
    </section>
);

StepSection.propTypes = {
    icon: PropTypes.node.isRequired,
    title: PropTypes.string.isRequired,
    hint: PropTypes.node,
    muted: PropTypes.bool,
    children: PropTypes.node,
};

StepSection.defaultProps = {
    hint: undefined,
    muted: false,
    children: null,
};

const DATE_TIME_FORMAT = "MM/DD/YYYY HH:mm";

const dayjsToPickerValue = (value) => (value ? dayjs(value).format(DATE_TIME_FORMAT) : "");

const pickerValueToDayjs = (value) => {
    if (!value) {
        return null;
    }

    const parsed = dayjs(value, DATE_TIME_FORMAT, true);
    return parsed.isValid() ? parsed : dayjs(value);
};

const CoverageDateRangeInput = ({ value, onChange, disabled, insideModal, t }) => {
    const [pickup, dropoff] = value ?? [];

    const updatePickup = (nextValue) => {
        const nextPickup = pickerValueToDayjs(nextValue);
        onChange?.([nextPickup, dropoff ?? null]);
    };

    const updateDropoff = (nextValue) => {
        const nextDropoff = pickerValueToDayjs(nextValue);
        onChange?.([pickup ?? null, nextDropoff]);
    };

    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
            <CustomDateTimePicker
                label={t("wizard.pickupDate")}
                value={dayjsToPickerValue(pickup)}
                onChange={updatePickup}
                placeholder={t("wizard.pickupDatePlaceholder")}
                timePlaceholder={t("wizard.timePlaceholder")}
                disabled={disabled}
                insideModal={insideModal}
                disablePast
            />
            <CustomDateTimePicker
                label={t("wizard.dropoffDate")}
                value={dayjsToPickerValue(dropoff)}
                onChange={updateDropoff}
                placeholder={t("wizard.dropoffDatePlaceholder")}
                timePlaceholder={t("wizard.timePlaceholder")}
                disabled={disabled || !pickup}
                insideModal={insideModal}
                minDate={pickup ? pickup.toDate() : undefined}
                disablePast={false}
            />
        </div>
    );
};

CoverageDateRangeInput.propTypes = {
    value: PropTypes.arrayOf(PropTypes.object),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    insideModal: PropTypes.bool,
    t: PropTypes.func.isRequired,
};

CoverageDateRangeInput.defaultProps = {
    value: undefined,
    onChange: undefined,
    disabled: false,
    insideModal: true,
};

const mapLocationToOption = (location, showTeqCoverageIcons) => {
    const value = getLocationMongoId(location);
    if (!value) return null;

    const searchLabel = getLocationLabel(location);
    const cityState = [location?.city, location?.state].filter(Boolean).join(", ") || searchLabel;

    if (!showTeqCoverageIcons) {
        return {
            value,
            searchLabel,
            label: searchLabel,
        };
    }

    return {
        value,
        searchLabel,
        label: (
            <div className="flex items-start gap-2 py-0.5">
                {location?.isInsuranceEnabled ? (
                    <BsShieldFillCheck
                        style={{ color: "#22c55e", fontSize: "0.9rem", flexShrink: 0, marginTop: 2 }}
                        title="TeqMobility covered"
                    />
                ) : (
                    <BsShieldFillExclamation
                        style={{ color: "#f59e0b", fontSize: "0.9rem", flexShrink: 0, marginTop: 2 }}
                        title="TeqMobility not covered"
                    />
                )}
                <div className="flex min-w-0 flex-col text-sm leading-snug">
                    {location?.address ? <span>{location.address}</span> : null}
                    <span className={location?.address ? "text-xs text-[#9b9ba5]" : undefined}>
                        {cityState}
                    </span>
                </div>
            </div>
        ),
    };
};

const SelectVehicleStep = ({
    provider,
    cars,
    carsLoading,
    locations,
    locationsLoading,
    locationId,
    datesSelected,
    isLocationCoveredForProvider,
    showTeqCoverageIcons,
    selectedCarId,
    onCarSelect,
}) => {
    const { t } = useTranslation("sellInsurance");
    const locationSelected = Boolean(locationId);

    const locationOptions = locations
        .map((location) => mapLocationToOption(location, showTeqCoverageIcons))
        .filter(Boolean);
    const selectedLocation = locations.find((item) => getLocationMongoId(item) === locationId);

    const vehicleHint = !locationSelected
        ? t("wizard.selectLocationFirst")
        : !datesSelected
          ? t("wizard.selectDatesFirst")
          : provider === "teqmobility"
            ? t("wizard.teqCarsOnlyHint")
            : t("wizard.carValueHint");
    const noCarsMessage = provider === "teqmobility" ? t("wizard.noTeqCars") : t("wizard.noCars");
    const datesLocked = !locationSelected || !isLocationCoveredForProvider;
    const vehiclesReady = locationSelected && datesSelected && isLocationCoveredForProvider;

    return (
        <div className="space-y-4">
            <StepSection
                icon={<EnvironmentOutlined />}
                title={t("wizard.pickupLocation")}
                hint={showTeqCoverageIcons ? t("wizard.locationCoverageHint") : undefined}>
                <Form.Item
                    name="locationId"
                    className="mb-0"
                    rules={[{ required: true, message: t("wizard.fieldRequired") }]}>
                    <Select
                        size="large"
                        showSearch
                        loading={locationsLoading}
                        placeholder={t("wizard.pickupLocationPlaceholder")}
                        optionFilterProp="searchLabel"
                        options={locationOptions}
                        labelRender={() => {
                            if (!selectedLocation) return null;

                            const textLabel = getLocationLabel(selectedLocation);

                            if (!showTeqCoverageIcons) {
                                return <span>{textLabel}</span>;
                            }

                            return (
                                <div className="flex items-center gap-2">
                                    {selectedLocation.isInsuranceEnabled ? (
                                        <BsShieldFillCheck style={{ color: "#22c55e", fontSize: "0.9rem" }} />
                                    ) : (
                                        <BsShieldFillExclamation style={{ color: "#f59e0b", fontSize: "0.9rem" }} />
                                    )}
                                    <span>{textLabel}</span>
                                </div>
                            );
                        }}
                    />
                </Form.Item>

                {locationSelected && provider === "teqmobility" && !isLocationCoveredForProvider && (
                    <Alert
                        className="mt-3"
                        type="warning"
                        showIcon
                        message={t("wizard.locationNotCoveredTitle")}
                        description={t("wizard.locationNotCoveredDescription")}
                    />
                )}
            </StepSection>

            <StepSection
                icon={<CalendarOutlined />}
                title={t("wizard.coveragePeriod")}
                hint={t("wizard.coveragePeriodHint", { provider: PROVIDER_LABELS[provider] ?? "" })}
                muted={datesLocked}>
                <Form.Item
                    name="dateRange"
                    className="mb-0"
                    rules={[
                        { required: true, message: t("wizard.fieldRequired") },
                        {
                            validator: (_, value) => {
                                const [pickup, dropoff] = value ?? [];
                                if (!pickup || !dropoff) {
                                    return Promise.reject(new Error(t("wizard.fieldRequired")));
                                }
                                if (!dropoff.isAfter(pickup)) {
                                    return Promise.reject(new Error(t("wizard.dropoffAfterPickup")));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}>
                    <CoverageDateRangeInput disabled={datesLocked} insideModal t={t} />
                </Form.Item>
            </StepSection>

            <StepSection icon={<CarOutlined />} title={t("wizard.vehicle")} hint={vehicleHint} muted={!vehiclesReady}>
                <Form.Item
                    name="carId"
                    className="mb-0 [&_.ant-form-item-control-input]:min-h-0"
                    rules={[{ required: true, message: t("wizard.fieldRequired") }]}>
                    <input type="hidden" />
                </Form.Item>

                {vehiclesReady ? (
                    carsLoading ? (
                        <div className="flex justify-center py-8">
                            <Spin />
                        </div>
                    ) : cars.length === 0 ? (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={noCarsMessage} />
                    ) : (
                        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                            {cars.map((car) => {
                                const carId = car.id ?? car._id;
                                return (
                                    <SellableCarCard
                                        key={carId}
                                        car={car}
                                        selected={carId === selectedCarId}
                                        onSelect={onCarSelect}
                                    />
                                );
                            })}
                        </div>
                    )
                ) : null}
            </StepSection>
        </div>
    );
};

SelectVehicleStep.propTypes = {
    provider: PropTypes.string,
    cars: PropTypes.arrayOf(PropTypes.object).isRequired,
    carsLoading: PropTypes.bool,
    locations: PropTypes.arrayOf(PropTypes.object).isRequired,
    locationsLoading: PropTypes.bool,
    locationId: PropTypes.string,
    datesSelected: PropTypes.bool,
    isLocationCoveredForProvider: PropTypes.bool,
    showTeqCoverageIcons: PropTypes.bool,
    selectedCarId: PropTypes.string,
    onCarSelect: PropTypes.func.isRequired,
};

SelectVehicleStep.defaultProps = {
    provider: undefined,
    carsLoading: false,
    locationsLoading: false,
    locationId: undefined,
    datesSelected: false,
    isLocationCoveredForProvider: true,
    showTeqCoverageIcons: false,
    selectedCarId: undefined,
};

export default SelectVehicleStep;
